import * as Sentry from "@sentry/react";

export function initSentry() {
  // Sentry DSN is a publishable key (safe to include in frontend code)
  const dsn = "https://0a21bf0eac7c08040c55bd8f5c2c68c1@o4510828551602256.ingest.de.sentry.io/4510828551602256";

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    
    // Performance monitoring - sample 10% of transactions
    tracesSampleRate: 0.1,
    
    // Session replay - capture 10% of sessions, 100% on error
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    
    // Filter out noisy errors
    ignoreErrors: [
      // Browser extensions
      /chrome-extension/,
      /moz-extension/,
      // Network errors users can't control
      "Network request failed",
      "Failed to fetch",
      "Load failed",
      // Cancelled requests
      "AbortError",
      // Auth state changes
      "AuthSessionMissingError",
    ],
    
    // Don't send PII
    beforeSend(event) {
      // Remove IP addresses
      if (event.user) {
        delete event.user.ip_address;
      }
      return event;
    },
  });
}

// Error boundary wrapper for React components
export const SentryErrorBoundary = Sentry.ErrorBoundary;

// Manual error capture
export function captureError(error: Error, context?: Record<string, unknown>) {
  Sentry.captureException(error, {
    extra: context,
  });
}

// Set user context for better debugging
export function setUserContext(userId: string, email?: string) {
  Sentry.setUser({
    id: userId,
    email: email,
  });
}

// Set subscription context for error tracking
export function setSubscriptionContext(plan: string | null, isSubscribed: boolean) {
  Sentry.setTag("subscription_plan", plan || "free");
  Sentry.setTag("is_subscribed", String(isSubscribed));
}

// Set page/feature context
export function setFeatureContext(feature: string) {
  Sentry.setTag("feature", feature);
}

// Clear user on logout
export function clearUserContext() {
  Sentry.setUser(null);
}

// Test function to verify Sentry integration
export function testSentryIntegration() {
  try {
    throw new Error("Sentry test error - integration verification");
  } catch (error) {
    Sentry.captureException(error, {
      tags: {
        test: "manual_verification",
      },
    });
    console.log("✅ Test error sent to Sentry - check your dashboard!");
    return true;
  }
}

// Make test function available in browser console
if (typeof window !== "undefined") {
  (window as unknown as Record<string, unknown>).testSentry = testSentryIntegration;
}
